import React from "react";
import ReactDOM from "react-dom/client";
import { getStatus } from "../api/statuses";
import ChecklistsContainer from "../components/ChecklistInLeadCard/ChecklistContainer";
import { getCustomFieldsStatuses } from "../api/cf_statuses";
import { getCurrLeadId } from "../amo/leads";
import { restoreAllAmoFields } from "../services/amoFieldMountService";

const FIELDS_GROUP_SELECTOR = ".linked-forms__group-wrapper.linked-forms__group-wrapper_main.js-cf-group-wrapper";
const CHECKLIST_CONTAINER_SELECTOR = '[data-id="checklists-container"]';
const CARD_TAB_SELECTOR = [
    "[role='tab']",
    "[aria-selected]",
    "[class*='tab']",
    "[class*='tabs']",
    "div[class*='card']",
    "div[class*='linked']",
    "a",
    "button",
    "li",
    "span",
].join(",");
const MAIN_FIELDS_TAB_TEXT = "основное";
const DEFAULT_FIELDS_GROUP_IDS = new Set([
    "",
    "default",
    "main",
    "common",
    "base",
    "leads_default",
    "leads_main",
]);
const ACTIVE_TAB_CLASS_PARTS = ["active", "selected", "current"];
const INITIAL_MOUNT_DELAYS = [0, 100, 250, 500, 1000, 1500, 2500, 4000];
const CLICK_MOUNT_DELAYS = [80, 250, 600];

const getSubdomain = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    if (parts.length >= 2 && parts[0] !== 'www') {
        return parts[0];
    }
    return null;
};

export const getVisibleFieldsContainer = () => {
    return Array.from(document.querySelectorAll(FIELDS_GROUP_SELECTOR)).find(
        (element) => getComputedStyle(element).display !== "none"
    );
};

const normalizeTabText = (value = "") => value.trim().replace(/\s+/g, " ").toLowerCase();

const isElementShown = (element) => {
    if (!element || !element.isConnected) {
        return false;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
        return false;
    }

    const style = getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden" && style.visibility !== "collapse";
};

const hasActiveTabClass = (element) => (
    Array.from(element.classList || []).some((className) => (
        ACTIVE_TAB_CLASS_PARTS.some((part) => className.toLowerCase().includes(part))
    ))
);

export const isMainFieldsTabActive = () => {
    const mainTab = Array.from(document.querySelectorAll(CARD_TAB_SELECTOR)).find((element) => {
        const text = normalizeTabText(element.textContent || "");
        return text === MAIN_FIELDS_TAB_TEXT && isElementShown(element);
    });

    if (!mainTab) {
        return false;
    }

    if (mainTab.getAttribute("aria-selected") === "true") {
        return true;
    }

    if (mainTab.dataset.active === "true" || mainTab.dataset.selected === "true") {
        return true;
    }

    if (hasActiveTabClass(mainTab) || hasActiveTabClass(mainTab.parentElement)) {
        return true;
    }

    const fontWeight = Number.parseInt(getComputedStyle(mainTab).fontWeight, 10);
    return Number.isFinite(fontWeight) && fontWeight >= 600;
};

export const getTargetFieldsContainer = () => {
    const subdomain = getSubdomain();

    if (subdomain === "mstore24") {
        const mstoreContainer = document.querySelector(
            `${FIELDS_GROUP_SELECTOR}[data-id="leads_7711753774887"]`
        );
        if (mstoreContainer) {
            return mstoreContainer;
        }
    }

    const fieldsContainers = Array.from(document.querySelectorAll(FIELDS_GROUP_SELECTOR));
    const defaultContainer = fieldsContainers.find((element) => {
        const dataId = (element.dataset.id || "").toLowerCase();
        return DEFAULT_FIELDS_GROUP_IDS.has(dataId);
    });

    if (defaultContainer) {
        return defaultContainer;
    }

    if (isMainFieldsTabActive()) {
        return getVisibleFieldsContainer() || null;
    }

    return null;
};

export const waitForTargetFieldsContainer = async ({
    attempts = 10,
    delayMs = 100,
} = {}) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        const container = getTargetFieldsContainer();
        if (container) {
            return container;
        }
        await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    return null;
};

export const attachAmoFieldPlaceholders = (checklists = []) => (
    checklists.map((checklist) => ({
        ...checklist,
        fields: (checklist.fields_ids || []).map((id) => ({
            fieldId: String(id),
        })),
    }))
);

const leadWindow = async (statusId) => {
    try {
        if (!statusId) {
            return () => {};
        }

        const leadId = getCurrLeadId();
        if (!leadId) {
            return () => {};
        }

        const [status, cfStatusesRaw] = await Promise.all([
            getStatus(statusId),
            getCustomFieldsStatuses(leadId)
        ]);

        const cfStatuses = Array.isArray(cfStatusesRaw) ? cfStatusesRaw : [];
        if (!status || !Array.isArray(status.checklists)) {
            return () => {};
        }

        status.checklists = attachAmoFieldPlaceholders(
            status.checklists.map((checklist) => ({
                ...checklist,
                custom_fields: Array.isArray(checklist.custom_fields)
                    ? checklist.custom_fields.map((cf) => {
                        const foundStatus = cfStatuses.find((cfStatus) => cfStatus.custom_field === cf.id);
                        return foundStatus
                            ? { ...cf, statusId: foundStatus.id, content: foundStatus.content }
                            : { ...cf, statusId: null, content: null };
                    })
                    : []
            }))
        );

        const mainElement = document.createElement("div");
        mainElement.setAttribute("data-id", "checklists-container");
        const root = ReactDOM.createRoot(mainElement);
        const mountTimers = new Set();

        root.render(
            <>
                <ChecklistsContainer status={status} />
            </>
        );

        const mountIntoTargetContainer = () => {
            const fieldsContainer = getTargetFieldsContainer();
            if (!fieldsContainer) {
                return false;
            }

            document.querySelectorAll(CHECKLIST_CONTAINER_SELECTOR).forEach((child) => {
                if (child !== mainElement) {
                    child.remove();
                }
            });

            if (mainElement.parentElement !== fieldsContainer) {
                fieldsContainer.appendChild(mainElement);
            }

            return true;
        };

        const scheduleMountAttempts = (delays) => {
            delays.forEach((delayMs) => {
                const timerId = window.setTimeout(() => {
                    mountTimers.delete(timerId);
                    mountIntoTargetContainer();
                }, delayMs);
                mountTimers.add(timerId);
            });
        };

        const handleDocumentClick = () => {
            scheduleMountAttempts(CLICK_MOUNT_DELAYS);
        };

        scheduleMountAttempts(INITIAL_MOUNT_DELAYS);
        document.addEventListener("click", handleDocumentClick, true);

        return () => {
            document.removeEventListener("click", handleDocumentClick, true);
            mountTimers.forEach((timerId) => window.clearTimeout(timerId));
            mountTimers.clear();
            root.unmount();
            restoreAllAmoFields();
            mainElement.remove();
        };
    } catch (error) {
        console.error("leadWindow error", error);
        return () => {};
    }
};

export default leadWindow;
